import { useActiveOverlay, useCashHistory, useDarkOverlayVisible, useDepotHistory, useStats } from "@/state/selectors";
import { useGameStore } from "@/state/gameStore";
import { OverlayType } from "@/types/state";
import { AnimatePresence, motion } from "framer-motion";
import DefaultContentContainer from "@/components/shared/DefaultContainer/DefaultContentContainer";
import { ProgressSlider } from "@/components/screens/PlayingScreen/components/Hud/components/ProgressSlider";
import SmileyIcon from "@/components/icons/SmileyIcon";
import MoneyStatDisplay from "@/components/screens/PlayingScreen/components/Hud/components/MoneyStatDisplay";
import HeartDisplay from "@/components/shared/HeartDisplay";

// Tweak these to fine-tune the feel between overlay closing and HUD reappearing
const HUD_ENTER_DELAY = 0.15; // seconds to wait before sliding in
const HUD_ENTER_DURATION = 0.3;
const HUD_EXIT_DURATION = 0.25;

// The HUD is positioned at top-14 (~56px). To slide fully off-screen above,
// we need to offset by its own height plus that top distance.
const hudVariants = {
    initial: { y: "calc(-100% - 56px)" },
    animate: {
        y: 0,
        transition: { duration: HUD_ENTER_DURATION, ease: "easeOut", delay: HUD_ENTER_DELAY },
    },
    exit: {
        y: "calc(-100% - 56px)",
        transition: { duration: HUD_EXIT_DURATION, ease: "easeIn" },
    },
} as const;

type HUDProps = {
    phaseReady: boolean;
};

export function HUD({ phaseReady }: HUDProps) {
    const stats = useStats();
    const activeOverlay = useActiveOverlay();
    const setOverlay = useGameStore((state) => state.setOverlay);
    const cashHistory = useCashHistory();
    const depotHistory = useDepotHistory();
    const darkOverlayVisible = useDarkOverlayVisible();
    const gameIsFinished = useGameStore((state) => state.gameIsFinished);

    const handleStatsClick = () => {
        setOverlay(OverlayType.Stats);
    };

    const isOverlayOpen = activeOverlay !== OverlayType.None;
    const shouldShow = phaseReady && !isOverlayOpen && !darkOverlayVisible && !gameIsFinished;

    return (
        <AnimatePresence>
            {shouldShow && (
                <motion.div
                    key="hud"
                    variants={hudVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="absolute top-15 w-full z-10"
                >
                    <div className="mx-4 mt-2">
                        <ProgressSlider />
                        {/* Cash and Depot Row */}
                        <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                            {/* Cash Box */}
                            <MoneyStatDisplay
                                value={stats.cash}
                                history={cashHistory}
                                type="cash"
                                onClick={handleStatsClick}
                            />
                            {/* Depot Box */}
                            <MoneyStatDisplay
                                value={stats.investments}
                                history={depotHistory}
                                type="depot"
                                onClick={handleStatsClick}
                            />

                            {/* Happiness Box */}
                            <DefaultContentContainer rounded="md" className="flex">
                                <button
                                    onClick={handleStatsClick}
                                    className="flex w-full cursor-pointer flex-col items-center justify-between gap-2 p-3"
                                >
                                    <div className="-mt-0.5 flex justify-start items-center w-full">
                                        <div className="font-bold text-white/65 mr-0.5">Happiness</div>
                                        <SmileyIcon />
                                    </div>
                                    <HeartDisplay heartCount={stats.happiness} />
                                </button>
                            </DefaultContentContainer>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export default HUD;